/**
 * ProjectListPage - displays all projects in a grid view
 * Route: /projects
 */
import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Search, Archive, FolderKanban } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { PageHeader } from '@/components/common/PageHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { ProjectCard, ProjectListSkeleton } from '@/components/projects'
import { useProjects, useDeleteProject } from '@/api/projects'
import { usePermissions } from '@/hooks/usePermissions'

/**
 * Project list page for viewing and managing projects.
 * Displays projects in a responsive grid with search and filter capabilities.
 */
export function ProjectListPage() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [deleteProjectId, setDeleteProjectId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Permission checks
  const { hasPermission } = usePermissions()
  const canCreateProject = hasPermission('project:create')
  const canEditProject = hasPermission('project:update')
  const canDeleteProject = hasPermission('project:delete')

  // Fetch projects
  const {
    data: projectsData,
    isLoading,
    error: fetchError,
    refetch,
  } = useProjects({ includeArchived: true }) // Fetch all, filter client-side

  // Delete mutation
  const deleteProject = useDeleteProject()

  // Client-side filtering
  const filteredProjects = useMemo(() => {
    if (!projectsData?.projects) return []

    let projects = projectsData.projects

    // Filter archived unless showing them
    if (!includeArchived) {
      projects = projects.filter(p => !p.isArchived)
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      projects = projects.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      )
    }

    return projects
  }, [projectsData?.projects, includeArchived, searchQuery])

  const handleDeleteClick = (projectId: string) => {
    setDeleteProjectId(projectId)
    setDeleteError(null)
  }

  const handleEditClick = (projectId: string) => {
    navigate(`/projects/${projectId}/edit`)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteProjectId) return

    try {
      await deleteProject.mutateAsync({
        projectId: deleteProjectId,
        hardDelete: false // Soft delete (archive) by default
      })
      setDeleteProjectId(null)
      refetch()
    } catch (error) {
      setDeleteError(
        error instanceof Error
          ? error.message
          : 'Failed to archive project'
      )
    }
  }

  const handleDeleteCancel = () => {
    setDeleteProjectId(null)
    setDeleteError(null)
  }

  const projectToDelete = projectsData?.projects.find(
    p => p.id === deleteProjectId
  )

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        {/* Header */}
        <PageHeader
          title="Projects"
          description="Organize your sessions and runs with reusable constraints and context"
          actions={
            canCreateProject ? (
              <Button asChild>
                <Link to="/projects/new">
                  <Plus className="h-4 w-4 mr-2" />
                  New Project
                </Link>
              </Button>
            ) : undefined
          }
        />

        {/* Error Alert */}
        {fetchError && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Unable to load projects. Please try again later.
            </AlertDescription>
          </Alert>
        )}

        {/* Delete Error Alert */}
        {deleteError && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{deleteError}</AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            variant={includeArchived ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setIncludeArchived(!includeArchived)}
          >
            <Archive className="h-4 w-4 mr-2" />
            {includeArchived ? 'Hide Archived' : 'Show Archived'}
          </Button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div data-testid="project-loading">
            <ProjectListSkeleton count={6} />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredProjects.length === 0 && !fetchError && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <FolderKanban className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-2">No projects found</h3>
            <p className="text-muted-foreground mb-4 max-w-md">
              {searchQuery
                ? 'Try adjusting your search or filters.'
                : 'Projects help you organize sessions with reusable constraints and context.'}
            </p>
            {!searchQuery && canCreateProject && (
              <Button asChild>
                <Link to="/projects/new">
                  <Plus className="h-4 w-4 mr-2" />
                  Create your first project
                </Link>
              </Button>
            )}
          </div>
        )}

        {/* Project Grid */}
        {!isLoading && filteredProjects.length > 0 && (
          <ul
            role="list"
            aria-label="Projects"
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {filteredProjects.map((project) => (
              <li key={project.id}>
                <ProjectCard
                  project={project}
                  onEdit={canEditProject ? () => handleEditClick(project.id) : undefined}
                  onDelete={canDeleteProject ? () => handleDeleteClick(project.id) : undefined}
                />
              </li>
            ))}
          </ul>
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={!!deleteProjectId}
          onOpenChange={(open) => !open && handleDeleteCancel()}
          title="Archive Project?"
          description={
            projectToDelete
              ? `Are you sure you want to archive "${projectToDelete.name}"? The project and its sessions will no longer appear in lists, but you can restore it later.`
              : 'Are you sure you want to archive this project?'
          }
          confirmLabel="Archive"
          onConfirm={handleDeleteConfirm}
          variant="destructive"
        />
      </div>
    </PageShell>
  )
}
